import React from "react";

import styles from "./dateRangeInput.module.scss";

/**
 * A start/end pair for the `createdAt` filter.
 *
 * The two dates travel as ONE comma-separated string — "from,to" — because
 * every list page and every CSV export already passes `createdAt` straight
 * through to the server untouched. Adding a second field would have meant
 * threading it through six pages, the chip state and two exporters; the
 * server-side parser understands the pair instead.
 *
 *   ""            no filter
 *   "2026-09-01"  that day (what a saved single date still means)
 *   "from,to"     inclusive of both days
 *   "from,"       from that day onwards
 *   ",to"         up to and including that day
 */
const DateRangeInput = ({ value, onChange, className, ariaLabel = "Date" }) => {
  const [from = "", to = ""] = String(value || "").split(",");

  const emit = (nextFrom, nextTo) => {
    const start = (nextFrom || "").trim();
    const end = (nextTo || "").trim();
    // Both cleared means no filter at all, not an empty range — otherwise
    // clearing the pickers would still send "," and the list would keep
    // whatever the last query returned.
    onChange(!start && !end ? "" : `${start},${end}`);
  };

  return (
    <div className={styles.range}>
      <input
        type="date"
        className={className}
        value={from}
        // No `max`. Bounding one picker by the other greys out most of the
        // calendar, so picking a NEW range meant clearing the old one first —
        // the server swaps a reversed pair anyway, so nothing is lost by
        // letting either date be chosen in any order.
        onChange={(e) => emit(e.target.value, to)}
        aria-label={`${ariaLabel} from`}
      />
      <span className={styles.separator}>to</span>
      <input
        type="date"
        className={className}
        value={to}
        onChange={(e) => emit(from, e.target.value)}
        aria-label={`${ariaLabel} to`}
      />
    </div>
  );
};

export default DateRangeInput;
